import React, { useState } from 'react';
import { GoogleAuthProvider, sendEmailVerification, sendPasswordResetEmail, signInWithEmailAndPassword, signInWithPopup, signOut } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../../firebase-config';
import { useLocation, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { MODULES } from '../../data/curriculum';

const ADMIN_EMAILS = ['admin@theoperatormethod.com', 'rmcmanus@ramdesignworks.com', 'r0dmac522@gmail.com'];

function isAdminEmail(email) {
  const normalized = String(email || '').trim().toLowerCase();
  return ADMIN_EMAILS.includes(normalized);
}

// Returns /course/:moduleId/:lessonId for the next incomplete lesson, or course start
function getResumePoint(progress) {
  for (const mod of MODULES) {
    for (const lesson of mod.lessons) {
      if (!progress?.[mod.id]?.[lesson.id]?.completed) {
        return `/course/${mod.id}/${lesson.id}`;
      }
    }
  }
  return '/course/module-0/lesson-0';
}

export default function LoginPage() {
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const verifyEmailNotice = searchParams.get('verifyEmail') === '1';

  async function handleForgotPassword() {
    if (!email.trim()) {
      setError('Enter your email address above, then click Forgot password.');
      return;
    }
    setResetLoading(true);
    setError('');
    try {
      await sendPasswordResetEmail(auth, email.trim().toLowerCase());
      setResetSent(true);
    } catch (err) {
      if (err.code === 'auth/user-not-found') {
        // Don't reveal whether account exists — show same success message
        setResetSent(true);
      } else {
        setError('Could not send reset email. Please try again.');
      }
    } finally {
      setResetLoading(false);
    }
  }

  async function handleGoogleSignIn() {
    setError('');
    setGoogleLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      // Must be set BEFORE signInWithPopup — onAuthStateChanged fires before the promise resolves.
      sessionStorage.setItem('om_session_active', '1');
      const { user } = await signInWithPopup(auth, provider);

      // Self-heal: create Firestore profile if missing
      const userRef = doc(db, 'users', user.uid);
      const snap = await getDoc(userRef);
      if (!snap.exists()) {
        const displayName = user.displayName?.trim() || user.email?.split('@')[0] || 'Student';
        await setDoc(userRef, {
          name: displayName,
          displayName,
          email: user.email?.trim().toLowerCase() || '',
          tier: null,
          createdAt: serverTimestamp(),
        });
      }

      if (isAdminEmail(user.email)) {
        const redirect = searchParams.get('redirect');
        navigate(redirect || '/admin');
        return;
      }
      const profile = snap.exists() ? snap.data() : {};
      if (profile.tier) {
        navigate('/v2-course');
      } else {
        const returnTo = searchParams.get('returnTo');
        navigate(returnTo || '/pricing');
      }
    } catch (err) {
      const msgs = {
        'auth/popup-closed-by-user': 'Google sign-in was closed before completion.',
        'auth/cancelled-popup-request': 'Google sign-in was canceled.',
      };
      setError(msgs[err.code] || 'Google sign-in failed. Please try again.');
    } finally {
      setGoogleLoading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      // Must be set BEFORE signInWithEmailAndPassword — onAuthStateChanged fires before the promise resolves.
      sessionStorage.setItem('om_session_active', '1');
      const { user } = await signInWithEmailAndPassword(auth, email, password);
      // Mark this as an active browser session — cleared automatically when the browser closes.
      if (!isAdminEmail(user.email) && !user.emailVerified) {
        try {
          await sendEmailVerification(user);
        } catch {
          // Non-fatal — primary goal is to prevent unverified access.
        }
        await signOut(auth);
        setError('Please verify your email before signing in. A validation link has been sent to your inbox.');
        return;
      }
      // Admin always goes directly to the admin dashboard (or ?redirect= destination)
      if (isAdminEmail(user.email)) {
        const redirect = searchParams.get('redirect');
        navigate(redirect || '/admin');
        return;
      }
      // Fetch profile to decide where to send beta testers
      const snap = await getDoc(doc(db, 'users', user.uid));
      const profile = snap.exists() ? snap.data() : {};
      if (profile.betaTester) {
        if (!profile.courseStarted) {
          // First-ever course entry — go to lesson 1
          navigate('/course/module-0/lesson-0');
        } else {
          // Returning — resume exactly where left off
          navigate(getResumePoint(profile.progress), { state: { fromLogin: true } });
        }
      } else {
        // If user already has a paid tier, go straight to /chat
        // (don't follow returnTo=/pricing from the signup flow)
        if (profile.tier) {
          navigate('/v2-course');
        } else {
          const returnTo = searchParams.get('returnTo');
          navigate(returnTo || '/pricing');
        }
      }
    } catch (err) {
      switch (err.code) {
        case 'auth/user-not-found':
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
          setError('Invalid email or password. If you signed up with Google, use the "Sign in with Google" button above instead.');
          break;
        case 'auth/invalid-email':
          setError('Invalid email address.');
          break;
        case 'auth/too-many-requests':
          setError('Too many attempts. Try again in a few minutes, or use "Forgot password?" to reset.');
          break;
        case 'auth/user-disabled':
          setError('This account has been disabled. Contact support.');
          break;
        default:
          setError(`Sign-in failed (${err.code || 'unknown'}). Try Google sign-in or use Forgot password.`);
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{
      display: 'flex',
      minHeight: '100vh',
      fontFamily: "'Inter', -apple-system, sans-serif",
      backgroundColor: '#0a0e1a',
    }}>
      {/* Left panel — branding */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '60px',
        background: 'linear-gradient(135deg, #0a0e1a 0%, #0f1829 50%, #0a1628 100%)',
        borderRight: '1px solid #1e2a3a',
      }}>
        <div style={{ maxWidth: '480px' }}>
          <div style={{
            fontSize: '12px',
            letterSpacing: '0.25em',
            textTransform: 'uppercase',
            color: '#4f8ef7',
            marginBottom: '24px',
            fontWeight: 600,
          }}>
            The Operator Method
          </div>
          <h1 style={{
            fontSize: '48px',
            fontWeight: 700,
            color: '#f0f4ff',
            lineHeight: 1.15,
            margin: '0 0 24px 0',
          }}>
            Built for people<br />
            who get things done.
          </h1>
          <p style={{
            fontSize: '18px',
            color: '#6b7a99',
            lineHeight: 1.7,
            margin: 0,
          }}>
            A practical AI training course designed around real workflows —
            not theory, not hype. Learn to operate the tools at your disposal
            with precision and confidence.
          </p>
          <div style={{ marginTop: '48px', display: 'flex', gap: '32px' }}>
            {[['5', 'Modules'], ['21', 'Lessons'], ['3', 'Tracks']].map(([num, label]) => (
              <div key={label}>
                <div style={{ fontSize: '28px', fontWeight: 700, color: '#4f8ef7' }}>{num}</div>
                <div style={{ fontSize: '12px', color: '#4a5568', letterSpacing: '0.1em', textTransform: 'uppercase' }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel — form */}
      <div style={{
        width: '480px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '60px 48px',
        backgroundColor: '#0d1220',
      }}>
        <h2 style={{
          fontSize: '26px',
          fontWeight: 600,
          color: '#f0f4ff',
          margin: '0 0 8px 0',
        }}>
          Sign in
        </h2>
        <p style={{ color: '#6b7a99', margin: '0 0 36px 0', fontSize: '15px' }}>
          Welcome back. Pick up where you left off.
        </p>

        {verifyEmailNotice && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: 'rgba(79,142,247,0.08)',
            border: '1px solid rgba(79,142,247,0.2)',
            borderRadius: '8px',
            color: '#9fc3ff',
            fontSize: '14px',
            marginBottom: '20px',
            lineHeight: 1.5,
          }}>
            Email/password accounts must verify the inbox email before first access is granted.
          </div>
        )}

        {error && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#1a0f0f',
            border: '1px solid #5c1f1f',
            borderRadius: '8px',
            color: '#f87171',
            fontSize: '14px',
            marginBottom: '20px',
          }}>
            {error}
          </div>
        )}

        {/* Google sign-in */}
        <button
          type="button"
          onClick={handleGoogleSignIn}
          disabled={googleLoading || loading}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            width: '100%',
            padding: '12px',
            backgroundColor: '#141c2e',
            border: '1px solid #1e2a3a',
            borderRadius: '8px',
            color: '#f0f4ff',
            fontSize: '15px',
            fontWeight: 500,
            cursor: (googleLoading || loading) ? 'not-allowed' : 'pointer',
            marginBottom: '20px',
          }}
        >
          <svg width="18" height="18" viewBox="0 0 48 48">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.35-8.16 2.35-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
          {googleLoading ? 'Signing in...' : 'Sign in with Google'}
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
          <div style={{ flex: 1, height: '1px', backgroundColor: '#1e2a3a' }} />
          <span style={{ color: '#4a5568', fontSize: '12px', letterSpacing: '0.08em' }}>OR</span>
          <div style={{ flex: 1, height: '1px', backgroundColor: '#1e2a3a' }} />
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '13px', color: '#8892a4', marginBottom: '8px', letterSpacing: '0.05em' }}>
              EMAIL
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="you@example.com"
              style={{
                width: '100%',
                padding: '12px 14px',
                backgroundColor: '#141c2e',
                border: '1px solid #1e2a3a',
                borderRadius: '8px',
                color: '#f0f4ff',
                fontSize: '15px',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '13px', color: '#8892a4', marginBottom: '8px', letterSpacing: '0.05em' }}>
              PASSWORD
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                style={{
                  width: '100%',
                  padding: '12px 44px 12px 14px',
                  backgroundColor: '#141c2e',
                  border: '1px solid #1e2a3a',
                  borderRadius: '8px',
                  color: '#f0f4ff',
                  fontSize: '15px',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: '#4a5568',
                  cursor: 'pointer',
                  fontSize: '13px',
                  padding: 0,
                }}
              >
                {showPassword ? 'HIDE' : 'SHOW'}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              padding: '13px',
              backgroundColor: loading ? '#1e2a3a' : '#4f8ef7',
              color: loading ? '#4a5568' : '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.2s',
              marginTop: '4px',
            }}
          >
            {loading ? 'Signing in...' : 'Sign in'}
          </button>

          {resetSent ? (
            <div style={{
              padding: '10px 14px',
              backgroundColor: 'rgba(52,211,153,0.08)',
              border: '1px solid rgba(52,211,153,0.3)',
              borderRadius: '8px',
              color: '#6ee7b7',
              fontSize: '13px',
              lineHeight: 1.5,
              marginTop: '4px',
            }}>
              Password reset email sent. Check your inbox (and spam folder).
            </div>
          ) : (
            <button
              type="button"
              onClick={handleForgotPassword}
              disabled={resetLoading}
              style={{
                background: 'none',
                border: 'none',
                color: '#4a5a7a',
                fontSize: '13px',
                cursor: resetLoading ? 'not-allowed' : 'pointer',
                padding: '4px 0',
                textAlign: 'left',
                marginTop: '4px',
              }}
            >
              {resetLoading ? 'Sending...' : 'Forgot password?'}
            </button>
          )}
        </form>

        <p style={{ marginTop: '28px', textAlign: 'center', color: '#4a5568', fontSize: '14px' }}>
          Don't have an account?{' '}
          <Link to="/signup" state={{ backgroundLocation: location }} style={{ color: '#4f8ef7', textDecoration: 'none' }}>
            Sign up
          </Link>
        </p>
      </div>
    </div>
  );
}
